import cx from 'classnames';
import {map} from 'lodash';
import {useMemo} from 'react';

import {CopyText, DataWrapper} from 'components';
import {DateFormat, TimeFormat} from 'enums';
import {dateUtils, formatDate} from 'utils';

import {TableCellProps, TableHeadCellProps, TableProps, TableRowProps, TableTBodyProps, TableTHeadProps} from './types';

export const TableRow = ({children, className, selected, hoverable, light, ...rest}: TableRowProps) => (
    <tr
        className={cx(className, {
            'tw-bg-light': !selected || light,
            'tw-group-a tw-bg-primary-light tw-bg-opacity-25': selected,
            'tw-group': !selected && hoverable,
            'odd:tw-bg-opacity-50': !selected && !light
        })}
        {...rest}
    >
        {children}
    </tr>
);

export const TableCell = ({children, className, ellipsis, noYPadding, ...rest}: TableCellProps) => (
    <td
        {...rest}
        className={cx(
            className,
            {
                'tw-text-nowrap': ellipsis,
                'tw-break-words': !ellipsis,
                'tw-py-2': !noYPadding
            },
            'group-hover:tw-bg-primary-light group-hover:tw-bg-opacity-25',
            'vs-table-cell tw-px-5 tw-min-h-8 tw-border-b tw-border-default tw-bg-opacity-100'
        )}
    >
        {children}
    </td>
);

type IdCellProps = {
    item: {id: string, createdAt: Date | number | string};
    timeZone: string;
    dateFormat: DateFormat;
    timeFormat: TimeFormat;
    className?: string;
}

export const TableIdCell: React.FC<IdCellProps> = ({
    item,
    timeZone,
    dateFormat,
    timeFormat,
    className
}) => (
    <TableCell className={className}>
        <CopyText text={item.id} truncateLength={10} labelPlacement='left' iconSize='sm' />
        <div className='tw-text-sm tw-text-gray-500'>
            {formatDate(
                new Date(item.createdAt),
                timeZone,
                dateUtils.getDateTimeFormat(dateFormat, timeFormat)
            )}
        </div>
    </TableCell>
);

export const TableHeadCell = ({children, className, ...rest}: TableHeadCellProps) => (
    <th {...rest} className={cx(className, 'tw-text-left tw-px-5 tw-py-2 tw-font-normal tw-border-b tw-border-default')}>
        {children}
    </th>
);

export const TableBody = ({children, ...rest}: TableTBodyProps) => (
    <tbody {...rest}>{children}</tbody>
);

export const TableHead = ({children, ...rest}: TableTHeadProps) => (
    <thead {...rest}>{children}</thead>
);

export const Table: React.FC<TableProps> = ({
    className, headerNames, headers, columnsCount, loading, noData, children
}: TableProps) => {
    const colSpan = useMemo(() => headers?.length || headerNames?.length || columnsCount, [headers, headerNames, columnsCount]);
    return (
        <table className={cx(className, 'vs-table tw-table-fixed tw-w-full tw-border-collapse')}>
            {headerNames &&
                <TableHead>
                    <TableRow light>{map(headerNames, (column, index) => <TableHeadCell key={index}>{column}</TableHeadCell>)}</TableRow>
                </TableHead>}
            {headers &&
                <TableHead>
                    <TableRow light>{map(headers, (props, index) => <TableHeadCell key={index} {...props} />)}</TableRow>
                </TableHead>}

            {(loading || noData) && colSpan &&
                <TableBody>
                    <TableRow light>
                        <TableCell colSpan={colSpan} className='tw-h-24'>
                            <DataWrapper isLoading={loading} noData={noData} />
                        </TableCell>
                    </TableRow>
                </TableBody>}
            {!loading && !noData && children}
        </table>
    );
};
